import { useCallback, useEffect, useState } from "react";
import { createNatgw, deleteNatgw, getNatgwData } from "../services/aws/ec2";

/**
 * Custom hook for managing AWS NAT Gateway operations
 *
 * This hook provides functionality to create, delete, and check the status of AWS NAT Gateways.
 * It automatically polls for status updates at specified intervals after creation begins.
 *
 * @param {string} gatewayName - The name of the NAT gateway to manage
 * @param {number} refreshIntervalMs - Interval in milliseconds to check gateway status (default: 10000)
 * @returns {Object} An object containing:
 *   - isReady {boolean} - Whether the NAT gateway is available
 *   - isLoading {boolean} - Whether an operation is in progress
 *   - checkStatus {Function} - Function to manually check gateway status
 *   - createGateway {Function} - Function to create the NAT gateway
 *   - deleteGateway {Function} - Function to delete the NAT gateway
 */
export const useNatGateway = (gatewayName, refreshIntervalMs = 10000) => {
  const [isReady, setIsReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [shouldPoll, setShouldPoll] = useState(false);

  /**
   * Checks if the NAT Gateway is available
   *
   * @async
   * @returns {boolean} - Returns false if unauthorized or gateway not available
   */
  const checkStatus = useCallback(async () => {
    // Get gateway data from AWS API
    const natGatewayRes = await getNatgwData();
    const natGatewayJson = await natGatewayRes.json();

    // Handle unauthorized access
    if (natGatewayJson.status === 403) {
      setIsReady(false);
      return false;
    }

    // Check if our gateway exists and is in "available" state
    const isAvailable =
      natGatewayJson.status_data.find((gateway) => gateway.Name === gatewayName)?.Status === "available";

    // Update state based on gateway status
    setIsReady(isAvailable);
    return isAvailable;
  }, [gatewayName]);

  /**
   * Creates a new NAT Gateway with the specified name
   *
   * @async
   */
  const createGateway = useCallback(async () => {
    setIsLoading(true);
    try {
      await createNatgw(gatewayName);
      // Start polling once creation is initiated
      setShouldPoll(true);
      await checkStatus();
    } finally {
      setIsLoading(false);
    }
  }, [gatewayName, checkStatus]);

  /**
   * Deletes the NAT Gateway with the specified name
   *
   * @async
   */
  const deleteGateway = useCallback(async () => {
    setIsLoading(true);
    try {
      await deleteNatgw(gatewayName);
      setIsReady(false);
      // Stop polling after deletion
      setShouldPoll(false);
    } finally {
      setIsLoading(false);
    }
  }, [gatewayName]);

  // Set up interval to check status
  useEffect(() => {
    let intervalId = null;

    // Only poll if we've initiated the creation process and the gateway isn't ready yet
    if (shouldPoll && !isReady) {
      // Check status immediately
      checkStatus();

      // Set up refresh interval
      intervalId = setInterval(checkStatus, refreshIntervalMs);
      console.log("Started NAT gateway status polling");
    }

    // Clean up on unmount or when changing polling conditions
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
        console.log("Stopped NAT gateway status polling");
      }
    };
  }, [checkStatus, refreshIntervalMs, isReady, shouldPoll]);

  return {
    isReady,
    isLoading,
    checkStatus,
    createGateway,
    deleteGateway,
  };
};
